import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, message,Popover, Tooltip, Icon, Tabs } from 'antd';
import { ButtonGroup } from 'react-rainbow-components';
import {
  cancellablePromise,
  useCancellablePromise,
} from '../ComplexDocumentDesign/cancellablePromise';
import { SummaryContainer, TabMenuContainer, TabMenuHeader } from '../SidebarMenu/styles';
import { CustomTabMenuItemContainer as TabMenuItemContainer } from './styles';
import { BackgroundColorBox, PageBackgroundPicker } from '../ComplexDocumentDesign/styles';
import ColorPicker from '../../artboard/ColorPicker';
import { EditableCell } from '../ComplexDocumentDesign/CreativeBlockDesign/Charts/ChartSettingsSidebar/editableCell';
import { EMAIL_SETTINGS_TAB as trans } from '../SidebarMenu/translationValues';
import { COMPLEX_EMAIL_SETTINGS } from './translationValues';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../context/complexDocumentContext';
import {
  CONTEXT_KEYS,
  useArtboardContext,
} from '../../context/complexArtboardContext';
import { useEmailContext, COMPLEX_EMAIL_CONTEXT_KEYS } from '../../context/complexEmailContext';
import { verifiedFromAddressList, patchFromAddress, patchEnvFromAddress } from '../../utils/services/complexEmailAPI';
import FileSettings from './FileSettings';
import { FontFamilyInput, SizeButton, SizeContainer } from '../SidebarMenu/styles';
import FontSideBar from '../ComplexDocumentDesign/FontSideBar';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';
import { fontSizes } from '../ComplexDocumentDesign/TextBlockToolbar';
import { licenseDetails } from '../../utils/services/onPremAPI';
import { ENTERPRISE_ENV } from '../../utils/helper';
import AccessibilitySummaryEditor from '../SidebarMenu/AccessibilitySummaryEditor';
import { ComplexBlockSettingsTabsContainer, StyledTabPane } from '../../components/ComplexBlockSettings/styles';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../context/complexVersionContext';
import { COMPLEX_DOCUMENT_VERSION } from '../../utils/complexDocumentConstants';
import CategoryInput from '../ComplexDocumentDesign/SettingsTab/CategoryInput';

const { Option } = Select;
const NAMESPACES = ['EMAIL_SETTINGS_TAB', 'COMPLEX_EMAIL_SETTINGS'];

const getMarginName = marginIndex => {
  switch (marginIndex) {
    case 0:
      return 'marginTop';
    case 1:
      return 'marginRight';
    case 2:
      return 'marginBottom';
    case 3:
      return 'marginLeft';
    default:
      return null;
  }
};

const versionEnv = [COMPLEX_DOCUMENT_VERSION.QA_READY, COMPLEX_DOCUMENT_VERSION.PUBLISHED]

const ComplexEmailMetaTab = (props) => {
  const { t } = useTranslation(NAMESPACES);
  const { CurrentDesign } = props;
  const designSummarySelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY], []);
  const [summary] = useDocumentContext(designSummarySelector, ``);

  const [loading, setLoading] = useState(true);
  const [fromAddressList, setFromAddressList] = useState([]);

  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [, methods] = useDocumentContext(documentIdSelector, `ComplexEmailMetaTab`);

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector, 'ComplexEmailMetaTab');

  const fromAddressSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.FROM_ADDRESS], []);
  const [fromAddressId, emailMethods] = useEmailContext(fromAddressSelector, 'ComplexEmailMetaTab');

  const draftEditableSelector = useCallback(store => store[CONTEXT_KEYS.IS_DRAFT_EDITABLE], []);
  const [isDraftEditable] = useArtboardContext(draftEditableSelector, `ApprovalProcess`);

  const emailConfiguredSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.EMAIL_CONFIGURED], []);
  const [isEmailConfigured] = useEmailContext(emailConfiguredSelector, 'ComplexEmailMetaTab');

  const templateIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [emailTemplateId] = useDocumentContext(templateIdSelector, 'ComplexEmailMetaTab');

  const changes = useRef({});

  const currentPageSelector = useCallback(store => store.flows ? store.flows[0] : undefined, []);
  const [currentPageUUID] = useDocumentContext(currentPageSelector, `ComplexEmailMetaTab`);

  const readonlySelector = useCallback(store => store[CONTEXT_KEYS.READONLY_TEMPLATE], []);
  const [readOnly] = useArtboardContext(readonlySelector, 'ComplexEmailMetaTab');

  // Selectors for scopeType and scopeId calculation
  const groupIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_ID], []);
  const [groupId] = useDocumentContext(groupIdSelector, `ComplexEmailMetaTab`);

  const communicationIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.COMMUNICATION_ID], []);
  const [communicationId] = useDocumentContext(communicationIdSelector, `ComplexEmailMetaTab`);

  // Helper function to calculate scopeType based on CurrentDesign
  const getScopeType = useCallback((design) => {
    if (design === 'document') {
      return 'communication';
    } else if (design === 'flow') {
      return 'flow-group';
    } else if (design === 'block') {
      return 'block-email'; // Different from ComplexDocumentDesign
    }
    return 'flow-group'; // default fallback
  }, []);

  // Helper function to calculate scopeId based on CurrentDesign
  const getScopeId = useCallback((design) => {
    if (design === 'document') {
      return communicationId;
    } else {
      return groupId;
    }
  }, [groupId, communicationId]);

  const [fontListVisible, toggleFontList] = useState(false);
  const inputRef = useRef();
  const [showPicker, toggleShowPicker] = useState(false);
  const [picker, setPicker] = useState(false);

  const getDefaultStyleSelector = useCallback(
    store => ({ fontFamily: store.fontFamily, fontSize: store.fontSize }),
    [],
  );
  const [defaultStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const currentVisibleVersionSelector = useCallback(
    store => ({
      versionSettingsVisible: store[VERSION_CONTEXT_KEYS.VERSION_SETTINGS_VISIBLE],
      currentVisibleVersion: store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION],
    }),
    [],
  );
  const [{versionSettingsVisible, currentVisibleVersion }] = useVersionContext(
    currentVisibleVersionSelector,
  );

  const disableSendEmailFrom =  versionSettingsVisible && isDraftEditable ? versionEnv.includes(currentVisibleVersion) ? false : true  : !editable

  const {
    addCancellablePromise,
    removePendingPromise,
    clearPendingPromises,
  } = useCancellablePromise();

  const customSelector = useCallback(
    (store, previousState) => {
      return store[store.flows[0]];
    },
    [],
  );
  const [pageDetails] = useDocumentContext(customSelector, `ComplexEmailMetaTab`);

  const marginChanges = useRef(pageDetails?.margin);

  useEffect(() => {
    (async () => {
      const response = await verifiedFromAddressList();
      const SMTPIds = response?.data || [];
      let fromAddressIds = [];
      if(ENTERPRISE_ENV && isEmailConfigured === null) {
        const licenseInfo = await licenseDetails();
        if(licenseInfo?.EmailConfiguration) {
          fromAddressIds = [{ id: 0, emailFromAddress: t(COMPLEX_EMAIL_SETTINGS.EXTERNAL_DROPDOWN_OPTION.key, COMPLEX_EMAIL_SETTINGS.EXTERNAL_DROPDOWN_OPTION.value) }, ...SMTPIds];
        } else {
          fromAddressIds = SMTPIds;
        }
        emailMethods.set({
          [COMPLEX_EMAIL_CONTEXT_KEYS.EMAIL_CONFIGURED]: licenseInfo?.EmailConfiguration
        });
      } else if(ENTERPRISE_ENV && isEmailConfigured) {
        fromAddressIds = [{ id: 0, emailFromAddress: t(COMPLEX_EMAIL_SETTINGS.EXTERNAL_DROPDOWN_OPTION.key, COMPLEX_EMAIL_SETTINGS.EXTERNAL_DROPDOWN_OPTION.value) }, ...SMTPIds];
      } else {
        fromAddressIds = SMTPIds;
      }
      setFromAddressList(fromAddressIds);
      setLoading(false);
    })();
  }, []);

  const changeFromAddress = useCallback(async (e) => { // TODO : Check if publishing cycle should be invoked or alert should be shown like existing module
    let response = null;
    if(versionSettingsVisible && versionEnv.includes(currentVisibleVersion)){
      let env = currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.QA_READY ? 'testing' : 'published';
      response = await patchEnvFromAddress(emailTemplateId, e, env);
    } else {
      response = await patchFromAddress(emailTemplateId, e);
    }
    if (response.statusCode === 3111) {
      message.success(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.UPDATE_FROM_ADDRESS_SUCCESS.key}`, COMPLEX_EMAIL_SETTINGS.UPDATE_FROM_ADDRESS_SUCCESS.value));
      emailMethods.set({
        [COMPLEX_EMAIL_CONTEXT_KEYS.FROM_ADDRESS]: e
      });
      // TODO : Check if the change in from address has to be synced to send for testing
      /*updateArtboard({
        [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
          [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date()
        })
      });*/
    } else {
      message.error(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.UPDATE_FROM_ADDRESS_FAILURE.key}`, COMPLEX_EMAIL_SETTINGS.UPDATE_FROM_ADDRESS_FAILURE.value));
    }
  }, [currentVisibleVersion, emailTemplateId, versionSettingsVisible]);

  const updateHeightWidth = useCallback(
    async (value, size) => {
      if (value > 800) {
        // TODO : Show toast message on max width restriction
        return;
      }
      clearPendingPromises();
      const cancellablePromise1 = cancellablePromise(
        new Promise(resolve => setTimeout(resolve, 1000)),
      );
      changes.current = Object.assign({}, changes.current, {
        uuid: currentPageUUID,
        [size]: value,
      });
      addCancellablePromise(cancellablePromise1);
      console.log("On change ::", value);

      try {
        await cancellablePromise1.promise;
        console.log("Coming here ::", value, methods.setFlowSettings);
        methods.setFlowSettings('setPageWidth', {
          uuid: currentPageUUID,
          size: 'custom',
          widthInPX: value,
          widthInMM: value,
          orientation: 'portrait',
        });
      } catch (error) {
        console.error(error);
        removePendingPromise(cancellablePromise1);
      }
    },
    [currentPageUUID, pageDetails],
  );

  const onEmailSummarySave = useCallback(({contents, newGlobalVariables}) => {
    methods.updateSummary(contents, newGlobalVariables);
  }, []);

  const onWidthChange = useCallback(
    (a, b, value) => {
      if (!value || (value && Number(value) < 590)) {
        message.warn(
          t(
            `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.MIN_EMAIL_WIDTH_WARNING.key}`,
            COMPLEX_EMAIL_SETTINGS.MIN_EMAIL_WIDTH_WARNING.value,
          ),
        );
        return;
      } else if (value && Number(value) > 800) {
        message.warn(
          t(
            `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.MAX_EMAIL_WIDTH_WARNING.key}`,
            COMPLEX_EMAIL_SETTINGS.MAX_EMAIL_WIDTH_WARNING.value,
          ),
        );
        return;
      }
      if (value && Number(value) >= 590) {
        const callback = () => {
          // dispatch event to update text contents and it's styles in creative block and other text elements.
          setTimeout(
            () =>
              document.dispatchEvent(
                new CustomEvent('updateQuillContentsOnWidthChange', {
                  detail: { action: 'update', source: 'silent' },
                }),
              ),
            500,
          );
        };
        methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK]: callback });
        methods.setFlowSettings('setPageWidth', {
          uuid: currentPageUUID,
          size: 'custom',
          widthInPX: value,
          widthInMM: value,
          orientation: 'portrait',
        });
      }
    },
    [currentPageUUID],
  );

  const updateMargin = useCallback(
    async (value, marginIndex) => {
      let marginName = getMarginName(marginIndex);
      marginChanges.current = Object.assign({}, changes.current, {
        uuid: currentPageUUID,
        [marginName]: value,
      });

      methods.setFlowSettings('setPageMargin', marginChanges.current);
    },
    [currentPageUUID],
  );

  const onSizeChange = useCallback(newFontSize => {    
    if (!isNaN(newFontSize) && newFontSize > 0) {
      const designType = CurrentDesign === 'document' ? 'flow' : CurrentDesign;
      methods.updatefontFamilyAndSize(
        designType,
        newFontSize,
        defaultStyle.fontSize,
        'fontSize',
        'update',
      );
    }
  }, []);

  const changeFontFamily = useCallback(newFontFamily => {
    const designType = CurrentDesign === 'document' ? 'flow' : CurrentDesign;
    methods.updatefontFamilyAndSize(
      designType,
      newFontFamily,
      defaultStyle.fontFamily,
      'fontFamily',
      'update',
    );
  }, []);

  const onFontToggle = useCallback(() => {
    toggleFontList(prevState => {
      if (prevState) {
        document.getElementById('sidebar-absolute-close').style.visibility = 'hidden';
      } else {
        document.getElementById('sidebar-absolute-close').style.visibility = 'visible';
      }
      return !prevState;
    });
  }, []);

  const handleKeypress = e => {
    e.stopPropagation();
    if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const value = parseFloat(inputRef.current.innerHTML.trim());
      if (!isNaN(value) && value >= 1 && value <= 400) {
        onSizeChange(value);
      }
    }
  };

  const renderSize = () => {
    return (
      <span>
        <ButtonGroup style={{ border: '1px solid #d9d9d9', borderRadius: 5, padding: '0px 1px' }}>
          <SizeButton
            comboBox={1}
            isMinus={1}
            onClick={() => {
              if (!isNaN(defaultStyle.fontSize)) {
                onSizeChange(defaultStyle.fontSize - 1);
              }
            }}
            radiusLeft={true}
            icon="minus"
            disabled={!editable}
          />
          <Popover
            content={
              <SizeContainer>
                <ul>
                  {fontSizes.map(data => (
                    <li
                      key={data}
                      onClick={e => {
                        onSizeChange(parseInt(data?.replaceAll('px', '')) );
                      }}
                    >
                      {data}
                    </li>
                  ))}
                </ul>
              </SizeContainer>
            }
            title={null}
            trigger="click"
          >
            <div
              ref={inputRef}
              contentEditable={editable}
              suppressContentEditableWarning={true}
              className="size-ref ql-size"
              style={{
                width: '50px',
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#cdcdcd',
                ...( !editable && { cursor: 'not-allowed' })
              }}
              onKeyPress={handleKeypress}
              onBlur={e => {
                const value = parseFloat(inputRef.current.innerHTML.trim());
                if (
                  value !== defaultStyle.fontSize &&
                  !isNaN(value) &&
                  value >= 1 &&
                  value <= 400
                ) {
                  onSizeChange(value);
                }
              }}
            >
              {!isNaN(defaultStyle.fontSize) && defaultStyle.fontSize ? defaultStyle.fontSize : ' '}
            </div>
          </Popover>
          <SizeButton
            comboBox={1}
            isMinus={1}
            onClick={() => {
              if (!isNaN(defaultStyle.fontSize)) {
                onSizeChange(defaultStyle.fontSize + 1);
              }
            }}
            radiusLeft={false}
            icon="plus"
            disabled={!editable}
          />
        </ButtonGroup>
      </span>
    );
  };

  const onBackgroundColorClick = useCallback(() => {
    toggleShowPicker(true);
    setPicker('background');
  }, []);

  const onColorClick = useCallback(() => {
    toggleShowPicker(true);
    setPicker('color');
  }, []);

  const onColorPick = useCallback(
    color => {
      if (color && color.hex) {
        methods.setFlowSettings('setPageColor', {
          color: color.hex,
          uuid: currentPageUUID,
        });
      }
    },
    [currentPageUUID],
  );

  const onBackgroundColorPick = useCallback(
    color => {
      if (color && color.hex) {
        methods.setFlowSettings('setPageBackground', {
          backgroundColor: color.hex,
          uuid: currentPageUUID,
        });
      }
    },
    [currentPageUUID],
  );
 
  const renderEmailSettings =()=>{
    return(
       <StyledTabPane>
            <TabMenuContainer className="mailSettings" style={{ paddingTop: 10, overflowY: 'auto', height: '100%' }} disabled={disableSendEmailFrom}>
              {CurrentDesign === 'document' && !loading  && (
                <TabMenuItemContainer columnDirection={true} subject={1}>
                  <TabMenuHeader subject={1}> {t(trans.SEND_EMAIL_FROM_LABEL.key, trans.SEND_EMAIL_FROM_LABEL.value)} </TabMenuHeader>
                  <Select
                    defaultValue={fromAddressId}
                    value={fromAddressId}
                    style={{ width: '100%' }}
                    onChange={changeFromAddress}
                    disabled={disableSendEmailFrom}
                  >
                    {fromAddressList.map(address => {
                      return <Option value={address.id}>{address.emailFromAddress}</Option>;
                    })}
                  </Select>
                </TabMenuItemContainer>
              )}
              {!readOnly ? <TabMenuItemContainer columnDirection={true} subject={1} className="email-tag-settings">
                <FileSettings scopeType={getScopeType(CurrentDesign)} scopeId={getScopeId(CurrentDesign)} />
              </TabMenuItemContainer> : null}
              <CategoryInput templateId={emailTemplateId} isEmailTemplate={true} isEditMode={editable && !readOnly} />
              <TabMenuItemContainer>
                <TabMenuHeader>
                  {t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.CONTENT_STYLING_HEADING.key}`, COMPLEX_EMAIL_SETTINGS.CONTENT_STYLING_HEADING.value)}
                </TabMenuHeader>
              </TabMenuItemContainer>
              <TabMenuItemContainer style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginRight: 10 }}>
                  <TabMenuHeader className="subHeading">
                    {' '}
                    {t(
                      `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.MAXIMUM_WIDTH_OF_CONTENT_LABEL.key}`,
                      COMPLEX_EMAIL_SETTINGS.MAXIMUM_WIDTH_OF_CONTENT_LABEL.value,
                    )}
                    {' '}
                  </TabMenuHeader>
                  <EditableCell
                    type="number"
                    text={pageDetails?.width}
                    updateSource={onWidthChange}
                    max={800}
                    min={590}
                    updateType='onPressEnter'
                    disabled={!editable || CurrentDesign !== 'document'}
                  />
              </TabMenuItemContainer>
              {CurrentDesign === 'document' && (
                <>
                <TabMenuItemContainer style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginRight: 10 }}>
                  <TabMenuHeader className="subHeading">
                    {' '}
                    {t(
                      `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.EMAIL_BACKGROUND_LABEL.key}`,
                      COMPLEX_EMAIL_SETTINGS.EMAIL_BACKGROUND_LABEL.value,
                    )}
                    {' '}
                  </TabMenuHeader>
                    <BackgroundColorBox
                      style={{ backgroundColor: pageDetails?.backgroundColor, width: 125, height: 30 }}
                      onClick={editable && onBackgroundColorClick}
                      disabled={!editable}
                    >
                      {showPicker && picker === 'background' && (
                        <PageBackgroundPicker style={{ left: -188, top: -350, width: 220, height: 366 }}>
                          <ColorPicker
                            onChange={onBackgroundColorPick}
                            onBlur={() => toggleShowPicker(false)}
                            color={pageDetails?.backgroundColor}
                            qlEditor={true}
                          />
                        </PageBackgroundPicker>
                      )}
                    </BackgroundColorBox>
                </TabMenuItemContainer>
                <TabMenuItemContainer style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginRight: 10 }}>
                  <TabMenuHeader className="subHeading">
                    {' '}
                    {t(
                      `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.CONTENT_BACKGROUND_LABEL.key}`,
                      COMPLEX_EMAIL_SETTINGS.CONTENT_BACKGROUND_LABEL.value,
                    )}
                    {' '}
                  </TabMenuHeader>
                    <BackgroundColorBox
                      style={{ backgroundColor: pageDetails?.color, width: 125, height: 30 }}
                      onClick={editable && onColorClick}
                      disabled={!editable}
                    >
                      {showPicker && picker === 'color' && (
                        <PageBackgroundPicker style={{left: -188, top: -350, width: 220, height: 366}} >
                          <ColorPicker
                            onChange={onColorPick}
                            onBlur={() => toggleShowPicker(false)}
                            color={pageDetails?.color}
                            qlEditor={true}
                          />
                        </PageBackgroundPicker>
                      )}
                    </BackgroundColorBox>
                </TabMenuItemContainer>
              </>)}
              <TabMenuItemContainer style={{ justifyContent: 'space-between' }}>
                <TabMenuItemContainer columnDirection={true} style={{ width: CurrentDesign === 'document' ? 'auto' : '100%' }}>
                <TabMenuItemContainer columnDirection={true} style={{ alignItems: 'flex-start', width: '100%' }}>
                <TabMenuHeader>
                  {t(
                    `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.DEFAULT_EMAIL_FONT_FAMILY_LABEL.key}`,
                    COMPLEX_EMAIL_SETTINGS.DEFAULT_EMAIL_FONT_FAMILY_LABEL.value,
                  )}
                </TabMenuHeader>

                <FontFamilyInput complexEmailDesign>
                  <div onClick={() => editable && onFontToggle()} className="input" style={{ cursor: !editable && 'not-allowed' }}>
                    <span>{defaultStyle.fontFamily}</span>
                  </div>
                </FontFamilyInput>
                </TabMenuItemContainer>
                <TabMenuItemContainer columnDirection={true} style={{ alignItems: 'flex-start', width: '100%' }}>
                <TabMenuHeader>
                  {t(
                    `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.DEFAULT_EMAIL_FONT_SIZE_LABEL.key}`,
                    COMPLEX_EMAIL_SETTINGS.DEFAULT_EMAIL_FONT_SIZE_LABEL.value,
                  )}
                </TabMenuHeader>

                <div style={{ margin: '5px 0px 0px 10px' }}>{renderSize()}</div>
                </TabMenuItemContainer>
                </TabMenuItemContainer>
                {CurrentDesign === 'document' && (<TabMenuItemContainer columnDirection={true} style={{ justifyContent: 'flex-start', marginRight: 10 }}>
                <TabMenuItemContainer style={{ justifyContent: 'space-between', width: '100%' }}>
                  <TabMenuHeader className="subHeading">

                    <span style={{ display: 'block', textAlign: 'left' }}>
                    {t(
                      `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.TOP_LABEL.key}`,
                      COMPLEX_EMAIL_SETTINGS.TOP_LABEL.value,
                    )}
                    {' '}
                    </span>
                  </TabMenuHeader>
                  <EditableCell
                    type="number"
                    text={pageDetails ? pageDetails.margin[0] : 0}
                    updateSource={(a, b, value) => updateMargin(value, 0)}
                    disabled={!editable}
                    style={{ width: 60 }}
                  />
                </TabMenuItemContainer>
                <TabMenuItemContainer style={{ justifyContent: 'space-between', width: '100%' }}>
                  <TabMenuHeader className="subHeading">
                    {t(
                      `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.BOTTOM_LABEL.key}`,
                      COMPLEX_EMAIL_SETTINGS.BOTTOM_LABEL.value,
                    )}
                    {' '}
                  </TabMenuHeader>
                  <EditableCell
                    type="number"
                    text={pageDetails ? pageDetails.margin[2] : 0}
                    updateSource={(a, b, value) => updateMargin(value, 2)}
                    disabled={!editable}
                    style={{ width: 60 }}
                  />
                </TabMenuItemContainer>
                <TabMenuItemContainer style={{ justifyContent: 'space-between', width: '100%' }}>
                  <TabMenuHeader className="subHeading">
                    {t(
                      `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.LEFT_LABEL.key}`,
                      COMPLEX_EMAIL_SETTINGS.LEFT_LABEL.value,
                    )}
                    {' '}
                  </TabMenuHeader>
                  <EditableCell
                    type="number"
                    text={pageDetails ? pageDetails.margin[3] : 0}
                    updateSource={(a, b, value) => updateMargin(value, 3)}
                    disabled={!editable}
                    style={{ width: 60 }}
                  />
                </TabMenuItemContainer>
                <TabMenuItemContainer style={{ justifyContent: 'space-between', width: '100%' }}>
                  <TabMenuHeader className="subHeading">
                    {t(
                      `COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.RIGHT_LABEL.key}`,
                      COMPLEX_EMAIL_SETTINGS.RIGHT_LABEL.value,
                    )}
                    {' '}
                  </TabMenuHeader>
                  <EditableCell
                    type="number"
                    text={pageDetails ? pageDetails.margin[1] : 0}
                    updateSource={(a, b, value) => updateMargin(value, 1)}
                    disabled={!editable}
                    style={{ width: 60 }}
                  />
                </TabMenuItemContainer>
                </TabMenuItemContainer>)}
              </TabMenuItemContainer>
              <TabMenuItemContainer columnDirection={true} style={{ alignItems: 'flex-start' }}>
                {fontListVisible && (
                  <FontSideBar
                    itemUUID={undefined}
                    editor={undefined}
                    setFontChange={changeFontFamily}
                    fontFamily={defaultStyle.fontFamily}
                    isToolBarVisible={true}
                    closeFontList={onFontToggle}
                    isAbsolute={true}
                    isComplexEmailDesign={true}
                    customStyle={{ position: 'absolute', zIndex: 101, top: 70 }}
                  />
                )}
              </TabMenuItemContainer>
              <TabMenuItemContainer style={{ display: 'block', justifyContent: 'space-between', width: '100%' }}>
              </TabMenuItemContainer>
            </TabMenuContainer>
          </StyledTabPane>
    );
  }
  const { TabPane } = Tabs;

  return (
    <>
      <ComplexBlockSettingsTabsContainer>
        {CurrentDesign === 'document' ?         
        <Tabs tabBarStyle={{ color: 'white' }}>
          <TabPane tab={t(
            COMPLEX_EMAIL_SETTINGS.SETTINGS_TAB.key,
            COMPLEX_EMAIL_SETTINGS.SETTINGS_TAB.value,
          )} key="1">
          {renderEmailSettings()}
        </TabPane>
        <TabPane 
          tab={<>{t(COMPLEX_EMAIL_SETTINGS.ACCESSIBILITY_SUMMARY.key, COMPLEX_EMAIL_SETTINGS.ACCESSIBILITY_SUMMARY.value)}{' '}
            <Tooltip
              placement="top"
              title={t(COMPLEX_EMAIL_SETTINGS.ACCESSIBILITY_SUMMARY_TOOLTIP.key, COMPLEX_EMAIL_SETTINGS.ACCESSIBILITY_SUMMARY_TOOLTIP.value)}>
              <Icon type="question-circle" />
            </Tooltip></>}
          key="2">
              <SummaryContainer style={{ marginLeft: '6px' }}>
                <AccessibilitySummaryEditor
                  itemUUID={'email-summary'}
                  contents={summary?.contents || []}
                  customContainerStyle={{ marginTop: 10 }}
                  onSaveChanges={onEmailSummarySave}
                  customizeHistory={false}
                  customClassName={'summary-editor'}
                  disabled={!editable}
                />
              </SummaryContainer>
          </TabPane>
        </Tabs>:
          renderEmailSettings()}
      </ComplexBlockSettingsTabsContainer>
    </>
  );
};

export default ComplexEmailMetaTab;